import { mapValues } from 'lodash';
import { fetchLiveCommentsFailure, fetchLiveCommentsNotFound, fetchLiveCommentsSuccess, I_AM_TYPING, liveCommentsReceived, REVEAL_LIVE_COMMENTS, REVEAL_LIVE_REPLIES, someoneIsTyping, } from './live-updates-actions';
import { getUnresovedIdsByCtx, initLiveCommentsFetcher, makeFetchFn, } from './live-comments-fetcher';
import { initSubscriptionsManager, subscribe } from './live-updates-subscriptions';
import { filterOwnComments } from './wait-for-idle-create-requests';
import { initLiveCommentsThrottler } from './live-comments-throttler';
import { initSomeoneIsTypingManager } from './someone-is-typing-manager';
import { getExperiment } from '../../experiments/experiments-selectors';
import { EXPERIMENT_DISABLE_LIVE_UPDATES } from '../../experiments/experiments-constants';
import { getCurrentOwner } from '../../members/members-selectors';
const COMMENTS_DUPLEXER_APP_DEF_ID = '6b810794-1c57-4f29-9b50-3426855d6d50';
// Channels for resource are subscribed/unsubscribed declaratively by wathcing resource READY states by subscriptionsManager
//
// For new comments anouncements:
// duplexer events -> subscriptionsManager -> filterOwnComments -> liveCommentsThrottler -> redux action
//
// For new comments fetching:
// redux action (REVEAL_...) -> liveCommentsFetcher -> redux action
//
// For someone-is-typing:
// duplexer events -> subscriptionsManager -> someoneIsTypingManager -> redux actions
//
// For iAmTyping messages to duplexer
// redux action (I_AM_TYPING) -> subscriptionsManager -> duplexer events
export function createLiveUpdatesMiddleware({ duplexerClient, appDefId, onDuplexerError, commentsService, }) {
    return (middlewareApi) => {
        const liveCommentsThrottler = initLiveCommentsThrottler({
            timeout: 5000,
            emitTo: (messages) => {
                // We should dispatch on next even loop to not have infinite recursive loop
                setTimeout(() => {
                    middlewareApi.dispatch(liveCommentsReceived(messages));
                }, 1);
            },
        });
        const filterOwn = filterOwnComments({
            emitTo: liveCommentsThrottler,
            getCurrentOwner: () => getCurrentOwner(middlewareApi.getState()),
        });
        const fetchLiveComments = initLiveCommentsFetcher({
            emitSuccessTo: (payload) => middlewareApi.dispatch(fetchLiveCommentsSuccess(payload)),
            emitFailureTo: (payload) => middlewareApi.dispatch(fetchLiveCommentsFailure(payload)),
            emitNotFoundTo: (payload) => middlewareApi.dispatch(fetchLiveCommentsNotFound(payload)),
            fetchFn: makeFetchFn(commentsService, appDefId),
        });
        const someoneIsTypingManager = initSomeoneIsTypingManager({
            emitStartTo: (resourceId) => middlewareApi.dispatch(someoneIsTyping({ resourceId, subaction: 'started' })),
            emitEndTo: (resourceId) => middlewareApi.dispatch(someoneIsTyping({ resourceId, subaction: 'ended' })),
            timeout: 4000,
            getCurrentTypingStates: () => mapValues(middlewareApi.getState().comments, (resourceState) => resourceState?.type === 'READY'
                ? resourceState.effects?.someoneIsTyping || 'none'
                : undefined),
        });
        const subscriptionsManager = initSubscriptionsManager({
            connect: () => duplexerClient.connect({ appDefId: COMMENTS_DUPLEXER_APP_DEF_ID }),
            subscribe: subscribe({
                getState: middlewareApi.getState,
                emitCommentCreatedTo: filterOwn,
                emitTypingTo: someoneIsTypingManager.collect,
                appDefId,
                onDuplexerError,
            }),
        });
        return (next) => (action) => {
            const result = next(action);
            const isLiveUpdatesEnabled = !getExperiment(middlewareApi.getState(), EXPERIMENT_DISABLE_LIVE_UPDATES);
            subscriptionsManager.syncSubscriptions(isLiveUpdatesEnabled
                ? { type: 'ENABLED', readyResourceIds: getReadyResourceIds(middlewareApi.getState()) }
                : { type: 'DISABLED' });
            someoneIsTypingManager.dropNotReady();
            if (isLiveUpdatesEnabled && action.type === I_AM_TYPING) {
                subscriptionsManager.publishIAmTyping(action.payload.resourceId);
            }
            if ((isLiveUpdatesEnabled && action.type === REVEAL_LIVE_REPLIES) ||
                action.type === REVEAL_LIVE_COMMENTS) {
                const resourceState = middlewareApi.getState().comments[action.payload.resourceId];
                if (resourceState?.type === 'READY') {
                    fetchLiveComments({
                        unresolvedIdsByCtx: getUnresovedIdsByCtx(middlewareApi.getState(), appDefId),
                        withVotes: resourceState.configs.withVotes,
                        resourceId: action.payload.resourceId,
                    });
                }
            }
            return result;
        };
    };
}
function getReadyResourceIds(state) {
    return Object.keys(state.comments).filter((resourceId) => state.comments[resourceId]?.type === 'READY');
}
//# sourceMappingURL=live-updates-middleware.js.map